import React, { useState, useEffect } from 'react';
import { Save, X, Upload, Trash2, Camera, User, ArrowLeft } from 'lucide-react';
import { bailAgentService } from '../../../services/bailAgentService';

const AgentProfileEditor = ({ 
  agent, 
  isCreatingNew, 
  onSave, 
  onCancel,
  onBack,
  loading 
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    license_number: '',
    phone: '',
    bio: '',
    years_experience: '',
    specializations: [],
    status: 'available'
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [specializationInput, setSpecializationInput] = useState('');

  // Update form when agent changes
  useEffect(() => {
    if (isCreatingNew) {
      setFormData({
        full_name: '',
        license_number: '',
        phone: '',
        bio: '',
        years_experience: '',
        specializations: [],
        status: 'available'
      });
      setErrors({});
    } else if (agent) {
      setFormData({
        full_name: agent?.full_name || '',
        license_number: agent?.license_number || '',
        phone: agent?.phone || '',
        bio: agent?.bio || '',
        years_experience: agent?.years_experience?.toString() || '',
        specializations: agent?.specializations || [],
        status: agent?.status || 'available'
      });
      setErrors({});
    }
  }, [agent, isCreatingNew]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Add specialization
  const handleAddSpecialization = () => {
    const trimmed = specializationInput?.trim();
    if (trimmed && !formData?.specializations?.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        specializations: [...prev?.specializations, trimmed]
      }));
      setSpecializationInput('');
    }
  };

  // Remove specialization
  const handleRemoveSpecialization = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev?.specializations?.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData?.license_number?.trim()) {
      newErrors.license_number = 'License number is required';
    }
    
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    const experience = parseInt(formData?.years_experience);
    if (formData?.years_experience && (isNaN(experience) || experience < 0)) {
      newErrors.years_experience = 'Years of experience must be a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      // Prepare form data with proper types
      const submitData = {
        ...formData,
        years_experience: parseInt(formData?.years_experience) || 0
      };
      
      const result = await onSave?.(submitData);
      
      if (result === false) {
        // Error was handled by parent component
        return;
      }
      
    } catch (error) {
      console.error('Error saving agent profile:', error);
      setErrors({ submit: 'Failed to save agent profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (file) => {
    if (!agent?.id || isCreatingNew) {
      alert('Please save the agent profile first before uploading a photo.');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const result = await bailAgentService?.uploadProfilePhoto(agent?.id, file);
      
      if (result?.error) {
        alert(`Photo upload failed: ${result?.error}`);
        return;
      }
      
      // Trigger a refresh - the parent component should handle this
      window.location?.reload();
    } catch (error) {
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle photo removal
  const handlePhotoRemove = async () => {
    if (!agent?.id || isCreatingNew) return;
    
    setUploadingPhoto(true);
    
    try {
      const result = await bailAgentService?.removeProfilePhoto(agent?.id);
      
      if (result?.error) {
        alert(`Failed to remove photo: ${result?.error}`);
        return;
      }
      
      // Trigger a refresh - the parent component should handle this
      window.location?.reload();
    } catch (error) {
      alert('Failed to remove photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!isCreatingNew && !agent) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-12 text-center">
          <User className="mx-auto h-16 w-16 text-slate-400 mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Select an Agent</h3>
          <p className="text-slate-600 mb-6">
            Choose an agent from the roster to view and edit their profile, or create a new agent.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="border-b border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack || onCancel}
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-medium">Back to Roster</span>
            </button>
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            {isCreatingNew ? 'Add New Agent' : 'Edit Agent Profile'}
          </h2>
        </div>
      </div>
      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-6">
          
          {/* Error Message */}
          {errors?.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-600 text-sm">{errors?.submit}</div>
            </div>
          )}
          
          {/* Profile Photo Section */}
          {!isCreatingNew && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Profile Photo
              </label>
              <div className="flex items-center space-x-6">
                {agent?.profile_photo_url ? (
                  <div className="relative">
                    <img
                      src={agent?.profile_photo_url}
                      alt={agent?.full_name}
                      className="w-20 h-20 rounded-full border-4 border-white shadow-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={handlePhotoRemove}
                      disabled={uploadingPhoto}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <span className="text-white text-lg font-bold">
                      {agent?.full_name?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
                    </span>
                  </div>
                )}
                
                <div>
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e?.target?.files?.[0];
                      if (file) {
                        handlePhotoUpload(file);
                      }
                    }}
                    disabled={uploadingPhoto}
                    className="hidden"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="flex items-center space-x-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200 transition-colors"
                  >
                    {uploadingPhoto ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm font-medium">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Camera size={16} />
                        <span className="text-sm font-medium">Upload Photo</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData?.full_name}
                onChange={(e) => handleInputChange('full_name', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors?.full_name ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter full name"
              />
              {errors?.full_name && (
                <p className="text-red-600 text-sm mt-1">{errors?.full_name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                License Number *
              </label>
              <input
                type="text"
                value={formData?.license_number}
                onChange={(e) => handleInputChange('license_number', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors?.license_number ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="CA-1234567"
              />
              {errors?.license_number && (
                <p className="text-red-600 text-sm mt-1">{errors?.license_number}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors?.phone ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="(916) 555-0123"
              />
              {errors?.phone && (
                <p className="text-red-600 text-sm mt-1">{errors?.phone}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Years of Experience
              </label>
              <input
                type="number"
                value={formData?.years_experience}
                onChange={(e) => handleInputChange('years_experience', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors?.years_experience ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="0"
                min="0"
                max="50"
              />
              {errors?.years_experience && (
                <p className="text-red-600 text-sm mt-1">{errors?.years_experience}</p>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Status
            </label>
            <select
              value={formData?.status}
              onChange={(e) => handleInputChange('status', e?.target?.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="available">Available</option>
              <option value="off_duty">Off Duty</option>
              <option value="vacation">Vacation</option>
            </select>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Professional Bio
            </label>
            <textarea
              value={formData?.bio}
              onChange={(e) => handleInputChange('bio', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter professional background, experience, and specialties..."
            />
          </div>

          {/* Specializations */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Specializations
            </label>
            
            {/* Add new specialization */}
            <div className="flex space-x-2 mb-3">
              <input
                type="text"
                value={specializationInput}
                onChange={(e) => setSpecializationInput(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddSpecialization();
                  }
                }}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add a specialization (e.g., Emergency Bail, DUI Cases)"
              />
              <button
                type="button"
                onClick={handleAddSpecialization}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
            
            {/* Current specializations */}
            {formData?.specializations?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData?.specializations?.map((spec, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                  >
                    {spec}
                    <button
                      type="button"
                      onClick={() => handleRemoveSpecialization(index)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 mt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onCancel || onBack}
            disabled={saving}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || loading}
            className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>{isCreatingNew ? 'Create Agent' : 'Save Changes'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentProfileEditor;